import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';

import type {
  SendDisableUserEmailMutation,
  SendDisableUserEmailMutationVariables,
  SendEnableUserEmailMutation,
  SendEnableUserEmailMutationVariables,
  SendFeedbackEmailMutation,
  SendFeedbackEmailMutationVariables,
  UpdateUserNotificationConnectionMutation,
  UpdateUserNotificationConnectionMutationVariables,
  UpdateUserNotificationPreferenceMutation,
  UpdateUserNotificationPreferenceMutationVariables,
} from 'generated/notification.generated';
import {
  useGetUserNotificationConnectionsQuery,
  useSendDisableUserEmailMutation,
  useSendEnableUserEmailMutation,
  useSendFeedbackEmailMutation,
  useUpdateUserNotificationConnectionMutation,
  useUpdateUserNotificationPreferenceMutation,
} from 'generated/notification.generated';
import { FeedbackEmailType } from 'generated/types';

export function useSendFeedback(
  type: FeedbackEmailType
): [
  (
    params: SendFeedbackEmailMutationVariables
  ) => Promise<FetchResult<SendFeedbackEmailMutation>>,
  boolean,
] {
  let messageId = 'sendFeedback';
  switch (type) {
    case FeedbackEmailType.Emr_Connection:
      messageId = 'missingEmrConnection';
      break;
    case FeedbackEmailType.Information_Request:
      messageId = 'infoRequest';
      break;
    case FeedbackEmailType.Client_Feedback:
    default:
      messageId = 'sendFeedback';
      break;
  }

  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: messageId,
  });
  const [mutate, { loading: baseLoading }] = useSendFeedbackEmailMutation();
  const loading = baseLoading;

  async function sendFeedback(params: SendFeedbackEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });

    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.sendFeedbackEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [sendFeedback, loading];
}

export const useGetUserNotificationConnections = (params: {
  user_id?: string | null;
}) => {
  const { handleError } = useResponseHandler();
  const { user_id } = params;
  const response = useGetUserNotificationConnectionsQuery({
    variables: { input: { user_id: user_id ?? '' } },
    skip: !user_id,
  });
  const { data, error } = response;
  handleError(error);
  const connections = data?.getUserNotificationConnections.connections ?? [];
  return { ...response, data: connections };
};

export function useUpdateNotificationPreference(): [
  (
    params: UpdateUserNotificationPreferenceMutationVariables
  ) => Promise<FetchResult<UpdateUserNotificationPreferenceMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateUserNotification',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateUserNotificationPreferenceMutation();
  const loading = baseLoading;

  async function updateNotificationPreference(
    params: UpdateUserNotificationPreferenceMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateUserNotificationPreference && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateNotificationPreference, loading];
}

export function useUpdateNotificationConnection(): [
  (
    params: UpdateUserNotificationConnectionMutationVariables
  ) => Promise<FetchResult<UpdateUserNotificationConnectionMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateUserNotification',
  });
  const [mutate, { loading: baseLoading }] =
    useUpdateUserNotificationConnectionMutation();
  const loading = baseLoading;

  async function updateNotificationConnection(
    params: UpdateUserNotificationConnectionMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateUserNotificationConnection && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateNotificationConnection, loading];
}

export function useSendDisableUserEmail(): [
  (
    params: SendDisableUserEmailMutationVariables
  ) => Promise<FetchResult<SendDisableUserEmailMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'disableUserEmail',
  });
  const [mutate, { loading: baseLoading }] = useSendDisableUserEmailMutation();
  const loading = baseLoading;

  async function sendEmail(params: SendDisableUserEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.sendDisableUserEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [sendEmail, loading];
}

export function useSendEnableUserEmail(): [
  (
    params: SendEnableUserEmailMutationVariables
  ) => Promise<FetchResult<SendEnableUserEmailMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'enableUserEmail',
  });
  const [mutate, { loading: baseLoading }] = useSendEnableUserEmailMutation();
  const loading = baseLoading;

  async function sendEmail(params: SendEnableUserEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.sendEnableUserEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [sendEmail, loading];
}
